/**
 * OrderService
 *
 * RESPONSIBILITIES:
 * - Order lifecycle management (create, edit, cancel orders)
 * - Order matching engine with real-time price monitoring
 * - Order persistence and history tracking
 * - Market/limit order execution logic
 *
 * SHOULD NOT:
 * - Calculate option prices or volatility (delegates to OptionService/VolatilityService)
 * - Handle account balances or position management (delegates to AccountService)
 * - Perform Black-Scholes calculations or option mathematics
 * - Store price data or manage price feeds
 */

import { getPriceService } from '@hooks/usePrices';
import { AccountService } from '@services/accountService';
import { BidAskPrices, OptionCalculator } from '@services/optionService';
import type { Asset, Position } from '@shared/types';

import {
  CreateOrderParams,
  EditOrderParams,
  Order,
  OrderFill,
  OrderHistory,
} from './orderTypes';
import { volatilityService } from './volatilityService';

interface OrderServiceState {
  orders: Order[];
  fills: OrderFill[];
}

export type OrderEventType =
  | 'orderCreated'
  | 'orderCancelled'
  | 'orderFilled'
  | 'orderExpired';

export interface OrderEvent {
  type: OrderEventType;
  order: Order;
  fill?: OrderFill; // Only for orderFilled events
}

export class OrderService {
  private state: OrderServiceState = {
    orders: [],
    fills: [],
  };
  private subscribers = new Set<(state: OrderServiceState) => void>();
  private eventSubscribers = new Map<
    OrderEventType,
    Set<(event: OrderEvent) => void>
  >();
  private priceServiceUnsubscribe: (() => void) | null = null;
  private orderCheckTimeout: number | null = null;

  private static instance: OrderService | null = null;
  public static getInstance() {
    OrderService.instance ??= new OrderService();
    return OrderService.instance;
  }

  private constructor() {
    this.init();
  }

  private readonly LS_KEYS = {
    orders: 'yv_orders_v1',
    orderHistory: 'yv_order_history_v1',
  };

  /**
   * Initialize service - load from localStorage and setup
   */
  init() {
    this.loadFromStorage();
    this.setupPriceMonitoring();
    this.notify();
  }

  /**
   * Load orders and fills from localStorage
   */
  private loadFromStorage() {
    try {
      const ordersData = localStorage.getItem(this.LS_KEYS.orders);
      const historyData = localStorage.getItem(this.LS_KEYS.orderHistory);

      if (ordersData) {
        const orders = JSON.parse(ordersData);
        this.state.orders = Array.isArray(orders) ? orders : [];
      }

      if (historyData) {
        const history: Partial<OrderHistory> = JSON.parse(historyData);
        if (history.orders && history.fills) {
          this.state.fills = Array.isArray(history.fills) ? history.fills : [];
        }
      }
    } catch (error) {
      console.warn('[OrderService] Failed to load from localStorage:', error);
      this.state = { orders: [], fills: [] };
    }
  }

  /**
   * Save current state to localStorage
   */
  private saveToStorage() {
    try {
      // Save current open orders
      localStorage.setItem(
        this.LS_KEYS.orders,
        JSON.stringify(this.state.orders),
      );

      // Save order history (all orders + fills)
      const allOrders = [
        ...this.state.orders,
        ...this.state.orders.filter(order => order.status === 'FILLED'),
      ];

      const history: OrderHistory = {
        orders: allOrders,
        fills: this.state.fills,
      };

      localStorage.setItem(this.LS_KEYS.orderHistory, JSON.stringify(history));
    } catch (error) {
      console.warn('[OrderService] Failed to save to localStorage:', error);
    }
  }

  /**
   * Notify all subscribers of state changes
   */
  private notify() {
    const snapshot = this.getState();
    this.subscribers.forEach(callback => {
      try {
        callback(snapshot);
      } catch (error) {
        console.warn('[OrderService] Subscriber error:', error);
      }
    });
  }

  /**
   * Subscribe to order state changes
   */
  subscribe(callback: (state: OrderServiceState) => void) {
    this.subscribers.add(callback);
    callback(this.getState());
    return () => this.subscribers.delete(callback);
  }

  /**
   * Subscribe to specific order events (orderCreated, orderCancelled, orderFilled, orderExpired)
   */
  subscribeToEvent(
    eventType: OrderEventType,
    callback: (event: OrderEvent) => void,
  ) {
    if (!this.eventSubscribers.has(eventType)) {
      this.eventSubscribers.set(eventType, new Set());
    }
    this.eventSubscribers.get(eventType)!.add(callback);

    return () => {
      const subscribers = this.eventSubscribers.get(eventType);
      if (subscribers) {
        subscribers.delete(callback);
      }
    };
  }

  /**
   * Emit an order event to all subscribers of that event type
   */
  private emitEvent(event: OrderEvent) {
    const subscribers = this.eventSubscribers.get(event.type);
    if (subscribers) {
      subscribers.forEach(callback => {
        try {
          callback(event);
        } catch (error) {
          console.warn('[OrderService] Event subscriber error:', error);
        }
      });
    }
  }

  /**
   * Get current state snapshot
   */
  getState(): OrderServiceState {
    return {
      orders: [...this.state.orders],
      fills: [...this.state.fills],
    };
  }

  /**
   * Create a new order
   */
  createOrder(params: CreateOrderParams): Order {
    const order: Order = {
      id: `order_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`,
      asset: params.asset,
      type: params.type,
      strikePrice: params.strikePrice,
      quantity: params.quantity,
      orderType: params.orderType,
      limitPrice: params.limitPrice,
      side: params.side,
      expiryTimestamp: params.expiryTimestamp,
      createdTimestamp: Date.now(),
      status: 'PENDING',
      positionId: params.positionId,
    };

    // Validate order parameters
    if (order.orderType === 'LIMIT' && !order.limitPrice) {
      throw new Error('Limit price is required for LIMIT orders');
    }

    if (order.quantity <= 0) {
      throw new Error('Order quantity must be greater than 0');
    }

    if (order.strikePrice <= 0) {
      throw new Error('Strike price must be greater than 0');
    }

    // Add to orders list
    this.state.orders.push(order);
    this.saveToStorage();

    // Emit orderCreated event
    this.emitEvent({ type: 'orderCreated', order });

    this.notify();

    // Execute market orders immediately (no waiting for price updates)
    if (order.orderType === 'MARKET') {
      this.executeMarketOrder(order);
    }

    return order;
  }

  /**
   * Cancel an existing order
   */
  cancelOrder(orderId: string): boolean {
    const orderIndex = this.state.orders.findIndex(
      order => order.id === orderId,
    );

    if (orderIndex === -1) {
      return false;
    }

    const order = this.state.orders[orderIndex];

    // Can only cancel pending orders
    if (order.status !== 'PENDING') {
      return false;
    }

    // Update order status
    order.status = 'CANCELLED';

    // Save order before removing for event emission
    const cancelledOrder = { ...order };

    // Remove from active orders
    this.state.orders.splice(orderIndex, 1);

    this.saveToStorage();

    // Emit orderCancelled event
    this.emitEvent({ type: 'orderCancelled', order: cancelledOrder });

    this.notify();

    return true;
  }

  /**
   * Edit an existing order (cancels original and creates new one)
   */
  editOrder(orderId: string, editParams: EditOrderParams): Order | null {
    const existingOrder = this.state.orders.find(order => order.id === orderId);

    if (!existingOrder || existingOrder.status !== 'PENDING') {
      return null;
    }

    // Cancel the existing order
    const cancelled = this.cancelOrder(orderId);
    if (!cancelled) {
      return null;
    }

    // Create new order with updated parameters
    const newOrderParams: CreateOrderParams = {
      asset: existingOrder.asset,
      type: existingOrder.type,
      strikePrice: existingOrder.strikePrice,
      quantity: editParams.quantity ?? existingOrder.quantity,
      orderType: editParams.orderType ?? existingOrder.orderType,
      limitPrice: editParams.limitPrice ?? existingOrder.limitPrice,
      side: existingOrder.side,
      expiryTimestamp: existingOrder.expiryTimestamp,
      positionId: existingOrder.positionId,
    };

    return this.createOrder(newOrderParams);
  }

  /**
   * Get all open (pending) orders
   */
  getOpenOrders(): Order[] {
    return this.state.orders.filter(order => order.status === 'PENDING');
  }

  /**
   * Get complete order history
   */
  getOrderHistory(): OrderHistory {
    try {
      const historyData = localStorage.getItem(this.LS_KEYS.orderHistory);
      if (historyData) {
        const history: Partial<OrderHistory> = JSON.parse(historyData);
        return {
          orders: history.orders ?? [],
          fills: history.fills ?? [],
        };
      }
    } catch (error) {
      console.warn('[OrderService] Failed to load order history:', error);
    }

    return {
      orders: [],
      fills: [],
    };
  }

  /**
   * Get order by ID
   */
  getOrderById(orderId: string): Order | null {
    return this.state.orders.find(order => order.id === orderId) ?? null;
  }

  /**
   * Clean up expired orders
   */
  cleanupExpiredOrders() {
    const now = Date.now();
    const expiredOrders: Order[] = [];

    for (let i = this.state.orders.length - 1; i >= 0; i--) {
      const order = this.state.orders[i];
      if (order.expiryTimestamp <= now && order.status === 'PENDING') {
        order.status = 'EXPIRED';
        expiredOrders.push({ ...order });
        this.state.orders.splice(i, 1);
      }
    }

    if (expiredOrders.length > 0) {
      this.saveToStorage();

      // Emit orderExpired events for each expired order
      expiredOrders.forEach(order => {
        this.emitEvent({ type: 'orderExpired', order });
      });

      this.notify();
    }
  }

  /**
   * Setup price monitoring with 300ms debounce
   */
  private setupPriceMonitoring() {
    // Clean up existing subscription
    if (this.priceServiceUnsubscribe) {
      this.priceServiceUnsubscribe();
    }

    // Subscribe to price updates
    this.priceServiceUnsubscribe = getPriceService().subscribe(() => {
      this.debouncedOrderCheck();
    });
  }

  /**
   * Debounced order checking (300ms)
   */
  private debouncedOrderCheck() {
    if (this.orderCheckTimeout) {
      clearTimeout(this.orderCheckTimeout);
    }

    this.orderCheckTimeout = window.setTimeout(() => {
      this.checkOrderMatching();
    }, 300);
  }

  /**
   * Check all pending orders for matches and execute them
   */
  private checkOrderMatching() {
    const pendingOrders = this.getOpenOrders();

    for (const order of pendingOrders) {
      try {
        if (order.orderType === 'MARKET') {
          // Market orders should execute immediately
          this.executeMarketOrder(order);
        }
        if (order.orderType === 'LIMIT') {
          // Check if limit order can be filled and execute if matched
          this.processLimitOrder(order);
        }
      } catch (error) {
        console.warn('[OrderService] Error checking order:', order.id, error);
      }
    }

    // Clean up expired orders
    this.cleanupExpiredOrders();
  }

  /**
   * Execute a market order immediately
   */
  private executeMarketOrder(order: Order) {
    const prices = this.getOrderPricing(order);
    if (!prices) {
      console.warn(
        '[OrderService] Unable to get pricing for market order:',
        order.id,
      );
      return;
    }

    // For market orders, use ask price for buys, bid price for sells
    const fillPrice = order.side === 'BUY' ? prices.ask : prices.bid;
    this.executeOrder(order, fillPrice, 'MARKET_IMMEDIATE');
  }

  /**
   * Process a limit order - check if it can be filled and execute if matched
   */
  private processLimitOrder(order: Order) {
    if (!order.limitPrice) {
      return;
    }

    const prices = this.getOrderPricing(order);
    if (!prices) {
      return;
    }

    let shouldFill = false;
    let fillPrice = 0;

    if (order.side === 'BUY') {
      // Buy limit order fills when ask price <= limit price
      if (prices.ask <= order.limitPrice) {
        shouldFill = true;
        // Get best execution: pay ask price (better than or equal to limit)
        fillPrice = prices.ask;
      }
    } else {
      // Sell limit order fills when bid price >= limit price
      if (prices.bid >= order.limitPrice) {
        shouldFill = true;
        // Get best execution: receive bid price (better than or equal to limit)
        fillPrice = prices.bid;
      }
    }

    if (shouldFill) {
      this.executeOrder(order, fillPrice, 'LIMIT_MATCHED');
    }
  }

  /**
   * Calculate order book pricing (bid/ask/mid) for an option
   */
  public calculatePricing(
    asset: Asset,
    strikePrice: number,
    optionType: 'CALL' | 'PUT',
    expiryTimestamp: number,
    spotPriceOverride?: number,
    volatilityOverride?: number,
  ): BidAskPrices | null {
    const volatility = volatilityOverride ?? volatilityService.getVolatility(asset);
    if (!volatility) {
      return null; // Cannot price without volatility data
    }
    return OptionCalculator.calculatePricing(
      asset,
      strikePrice,
      optionType,
      expiryTimestamp,
      volatility,
      spotPriceOverride,
    );
  }

  public calculatePositionPricing(position: Position): BidAskPrices | null {
    return this.calculatePricing(
      position.asset,
      position.strikePrice,
      position.type,
      position.expiryTimestamp,
    );
  }

  /**
   * Get bid/ask pricing for an order (internal method)
   */
  private getOrderPricing(order: Order): BidAskPrices | null {
    return this.calculatePricing(
      order.asset,
      order.strikePrice,
      order.type,
      order.expiryTimestamp,
    );
  }

  /**
   * Execute an order and create fill record
   */
  private executeOrder(
    order: Order,
    fillPrice: number,
    executionMethod: 'MARKET_IMMEDIATE' | 'LIMIT_MATCHED',
  ) {
    // Create fill record
    const fill: OrderFill = {
      orderId: order.id,
      fillPrice,
      fillTimestamp: Date.now(),
      executionMethod,
    };

    // Update order status
    order.status = 'FILLED';

    // Remove from active orders
    const orderIndex = this.state.orders.findIndex(o => o.id === order.id);
    if (orderIndex !== -1) {
      this.state.orders.splice(orderIndex, 1);
    }

    // Add fill record
    this.state.fills.push(fill);

    // Save and notify
    this.saveToStorage();

    // Emit orderFilled event with both order and fill data
    this.emitEvent({ type: 'orderFilled', order: { ...order }, fill });

    this.notify();

    // Handle position creation/sale through AccountService
    try {
      const accountService = AccountService.getInstance();

      if (order.side === 'BUY') {
        // Create new position
        const position = accountService.buyOption({
          asset: order.asset,
          type: order.type,
          strikePrice: order.strikePrice,
          expiryTimestamp: order.expiryTimestamp,
          purchasePrice: fillPrice,
          quantity: order.quantity,
          purchaseTimestamp: fill.fillTimestamp,
        });

        if (position) {
          console.log(
            '[OrderService] BUY order executed and position created:',
            {
              orderId: order.id,
              positionId: position.id,
              fillPrice,
              executionMethod,
            },
          );
        } else {
          console.warn(
            '[OrderService] BUY order executed but position creation failed:',
            {
              orderId: order.id,
              fillPrice,
              reason: 'Insufficient balance or other AccountService error',
            },
          );
        }
      }
      if (order.side === 'SELL' && order.positionId) {
        // Sell existing position with proper market value calculation
        const position = accountService
          .getState()
          .positions.find((p: Position) => p.id === order.positionId);
        if (position) {
          const saleValue = this.calculateSellValue(position, fillPrice);
          if (!saleValue) {
            return;
          }
          const totalCost = position.purchasePrice * position.quantity;
          const pnl = saleValue - totalCost;

          // Record the sale transaction
          accountService.pushTx('FILL_ORDER', saleValue, {
            positionId: order.positionId,
            asset: position.asset,
            pnl: pnl,
            totalCost: totalCost,
            saleValue: saleValue,
            orderType: order.orderType,
          });

          // Remove position from account
          accountService.removePosition(order.positionId);

          console.log('[OrderService] SELL order executed and position sold:', {
            orderId: order.id,
            positionId: order.positionId,
            fillPrice,
            saleValue,
            pnl,
            executionMethod,
          });
        } else {
          console.warn('[OrderService] SELL order position not found:', {
            orderId: order.id,
            positionId: order.positionId,
          });
        }
      } else if (order.side === 'SELL') {
        console.warn('[OrderService] SELL order missing positionId:', {
          orderId: order.id,
          fillPrice,
        });
      }
    } catch (error) {
      console.error(
        '[OrderService] Error handling position for order execution:',
        {
          orderId: order.id,
          side: order.side,
          fillPrice,
          error,
        },
      );
    }
  }

  /**
   * Calculate the market value for selling a position using current volatility
   */
  private calculateSellValue(
    position: Position,
    _spotPrice: number,
  ): null | number {
    // Use OrderService's own pricing method for consistency
    const bidAskPricing = this.calculatePricing(
      position.asset,
      position.strikePrice,
      position.type,
      position.expiryTimestamp,
    );

    if (!bidAskPricing) {
      console.warn(
        `[OrderService] No volatility data for ${position.asset}, using purchase price`,
      );
      return position.purchasePrice * position.quantity;
    }

    return bidAskPricing.bid * position.quantity;
  }

  /**
   * Cleanup method to call when service is destroyed
   */
  destroy() {
    if (this.priceServiceUnsubscribe) {
      this.priceServiceUnsubscribe();
      this.priceServiceUnsubscribe = null;
    }

    if (this.orderCheckTimeout) {
      clearTimeout(this.orderCheckTimeout);
      this.orderCheckTimeout = null;
    }
  }
}

// Export singleton instance
export const orderService = OrderService.getInstance();
